import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import APIResponseModal from '../components/APIResponseModal';



const InvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  // Form state matching EXACTLY the fbr_validate_submit_test.js structure
  const [formData, setFormData] = useState({
    // Basic Invoice Information - EXACT match to working test
    invoiceType: "Sale Invoice",
    invoiceDate: new Date().toISOString().split('T')[0],

    // Seller Information - from user profile but with fallback to working values
    sellerNTNCNIC: user?.ntn_cnic || "3410126332317",
    sellerBusinessName: user?.business_name || "New Younas Autos",
    sellerProvince: "Punjab",
    sellerAddress: user?.address || "12 sms almansorah dc colony gujranwala",

    // Buyer Information - EXACT match to working test
    buyerNTNCNIC: "9999999",
    buyerBusinessName: "Dilshad Enterprises",
    buyerProvince: "Punjab",
    buyerAddress: "38A Gulberg Housing Scheme Sui Gas Road Gujranwala",
    buyerRegistrationType: "Unregistered",

    // Invoice Details - EXACT match to working test
    invoiceRefNo: "SUN002119",
    scenarioId: "SN020",

    // Items array - EXACT match to working test structure
    items: [{
      hsCode: "8711.6090",
      productDescription: "Motorcycles (including mopeds) and cycles fitted with an auxiliary motor, with or without side- cars; side- cars. > With electric motor for propulsion: > - - -  Other",
      rate: "1%",
      uoM: "units",
      quantity: 1,
      totalValues: 101,
      valueSalesExcludingST: 100,
      fixedNotifiedValueOrRetailPrice: 101.00,
      salesTaxApplicable: 1,
      salesTaxWithheldAtSource: 0,
      extraTax: 0,
      furtherTax: 0,
      sroScheduleNo: "",
      fedPayable: 0,
      discount: 0,
      saleType: "Electric Vehicle",
      sroItemSerialNo: ""
    }]
  });
  const formDefaultsRef = useRef(formData);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [invoice, setInvoice] = useState(null);

  // HS suggestions, UoM options and errors per item index
  const [hsSuggestions, setHsSuggestions] = useState({});
  const [uomErrors, setUomErrors] = useState({});
  // Global UoM list from reference_data.type = 'uom' -> [{ code, label }]
  const [uomOptionsGlobal, setUomOptionsGlobal] = useState([]);

  // API Response Modal state
  const [showAPIModal, setShowAPIModal] = useState(false);
  const [apiModalData, setApiModalData] = useState(null);

  // Load global UoM list from reference_data.uom for dropdowns
  useEffect(() => {
    const fetchGlobalUoM = async () => {
      try {
        const { data } = await apiClient.get('/reference-data/uom');
        const list = (data?.data || []).map(row => ({ code: row.code, label: row.description || row.code })).filter(x => x.code);
        setUomOptionsGlobal(list);
      } catch (e) {
        console.warn('Failed to load global UoM list', e);
      }
    };
    fetchGlobalUoM();
  }, []);


  const loadInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/invoice/${id}`);

      // API returns { message, invoice }
      const inv = response.data?.invoice || response.data;
      setInvoice(inv);

      console.log('🔍 EDIT: Loaded invoice data:', {
        seller_ntn_cnic: inv.seller_ntn_cnic,
        seller_ntn_cnic_type: typeof inv.seller_ntn_cnic,
        user_ntn_cnic: user?.ntn_cnic,
        user_ntn_cnic_type: typeof user?.ntn_cnic
      });

      // Prefer the stored JSON if present; it may already be an object
      const existingData = typeof inv.json_data === 'string' ? safeParse(inv.json_data) : (inv.json_data || {});

      // Fallback mapping from DB columns if JSON is missing/incomplete
      const fallback = {
        invoiceType: inv.invoice_type || 'Sale Invoice',
        invoiceDate: inv.invoice_date ? new Date(inv.invoice_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        // Always use user profile data for seller information to avoid encryption issues
        sellerNTNCNIC: user?.ntn_cnic || formDefaultsRef.current.sellerNTNCNIC,
        sellerBusinessName: user?.business_name || inv.seller_business_name || formDefaultsRef.current.sellerBusinessName,
        sellerProvince: user?.province || inv.seller_province || formDefaultsRef.current.sellerProvince,
        sellerAddress: user?.address || inv.seller_address || formDefaultsRef.current.sellerAddress,
        buyerNTNCNIC: inv.buyer_ntn_cnic || formDefaultsRef.current.buyerNTNCNIC,
        buyerBusinessName: inv.buyer_business_name || formDefaultsRef.current.buyerBusinessName,
        buyerProvince: inv.buyer_province || formDefaultsRef.current.buyerProvince,
        buyerAddress: inv.buyer_address || formDefaultsRef.current.buyerAddress,
        buyerRegistrationType: inv.buyer_registration_type || formDefaultsRef.current.buyerRegistrationType,
        invoiceRefNo: inv.invoice_ref_no || formDefaultsRef.current.invoiceRefNo,
        scenarioId: inv.scenario_id || formDefaultsRef.current.scenarioId,
        items: Array.isArray(inv.items) && inv.items.length > 0 ?
          inv.items.map(it => ({
            hsCode: it.hs_code || it.hsCode || '',
            productDescription: it.product_description || it.productDescription || '',
            rate: parseFloat(it.rate ?? it.sales_tax_rate ?? 0), // unit price, numeric
            uoM: it.uom || it.uoM || 'PCS',
            quantity: parseFloat(it.quantity || 1), // default to 1 to pass validation
            totalValues: parseFloat(it.total_values || it.totalValues || 0),
            valueSalesExcludingST: parseFloat(it.value_sales_excluding_st || it.valueSalesExcludingST || 0),
            fixedNotifiedValueOrRetailPrice: parseFloat(it.fixed_notified_value_or_retail_price || it.fixedNotifiedValueOrRetailPrice || 0),
            salesTaxApplicable: parseFloat(it.sales_tax_applicable || it.salesTaxApplicable || 0),
            salesTaxWithheldAtSource: parseFloat(it.sales_tax_withheld_at_source || it.salesTaxWithheldAtSource || 0),
            extraTax: parseFloat(it.extra_tax || it.extraTax || 0),
            furtherTax: parseFloat(it.further_tax || it.furtherTax || 0),
            sroScheduleNo: it.sro_schedule_no || it.sroScheduleNo || '',
            fedPayable: parseFloat(it.fed_payable || it.fedPayable || 0),
            discount: parseFloat(it.discount || 0),
            saleType: it.sale_type || it.saleType || 'Goods at standard rate (default)',
            sroItemSerialNo: it.sro_item_serial_no || it.sroItemSerialNo || ''
          })) : formDefaultsRef.current.items
      };

      // Merge: existing JSON takes precedence, but seller info always comes from user profile
      setFormData(prev => ({
        ...prev,
        ...fallback,
        ...existingData,
        // Always override seller information with current user profile data
        sellerNTNCNIC: user?.ntn_cnic || prev.sellerNTNCNIC,
        sellerBusinessName: user?.business_name || prev.sellerBusinessName,
        sellerProvince: user?.province || prev.sellerProvince,
        sellerAddress: user?.address || prev.sellerAddress
      }));

    } catch (error) {
      console.error('Failed to load invoice:', error);
      toast.error('Failed to load invoice');
      navigate('/app/invoices');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, user]);

  useEffect(() => {
    if (isEdit && id) {
      loadInvoice();
    }
  }, [id, isEdit, loadInvoice]);

  // Auto-call validate and submit when user opens the edit page for a specific invoice
  // Disable any auto-validation on open
  const [autoCalled, setAutoCalled] = useState(true);
  useEffect(() => {
    if (isEdit && id && invoice && !autoCalled) {
      // Ensure we run this only once per visit
      setAutoCalled(true);
      (async () => {
        try {
          console.log('🔁 Auto validate triggered for invoice', id);
          setValidating(true);
          const valRes = await apiClient.post(`/fbr-validate/validate`, { invoiceData: formData });
          console.log('🔁 Auto validate response', valRes.data);
          if (valRes.data?.success) {
            setInvoice(prev => ({ ...(prev || { id }), status: 'Validated' }));
            toast.success('Invoice validated');
          } else {
            toast.error('Validation failed');
          }
        } catch (e) {
          console.error('❌ Auto validate/submit error', e);
          toast.error('Auto validate/submit failed');
        } finally {
          setValidating(false);
          setSubmitting(false);
        }
      })();
    }
  }, [isEdit, id, invoice, autoCalled, formData]);

  // Fetch UoM options for an HS code
  const fetchUoMOptions = async (idx, hsCode) => {
    try {
      await apiClient.get(`/reference/uom-options/${encodeURIComponent(hsCode)}`);
      // Preserve UoM from Excel/user input; do not auto-correct even if not in options.
      // Options fetched here are only used for validation hints (validateUoMForItem).
      // No state change needed.
    } catch (e) {
      console.warn('Failed to fetch UoM options for', hsCode, e);
    }
  };

  // Validate UoM for a given item
  const validateUoMForItem = async (idx) => {
    const it = formData.items[idx];
    if (!it?.hsCode || !it?.uoM) return;
    try {
      const uomClean = (it.uoM ?? '').toString().trim();
      const { data } = await apiClient.post('/reference/validate-uom', { hsCode: it.hsCode, uom: uomClean });
      const isValid = data?.validation?.isValid;
      setUomErrors(prev => ({ ...prev, [idx]: isValid ? null : data?.validation }));
      if (!isValid) {
        toast.error('Invalid UoM for HS Code. See details.');
      }
    } catch (e) {
      console.warn('UoM validation failed', e);
    }
  };

  // Fetch HS description and set productDescription; also populate UoM options
  const resolveHSCode = async (idx, hsCode) => {
    if (!hsCode) return;
    try {
      // Get HS record from backend reference-data endpoint
      const { data } = await apiClient.get(`/reference-data/hs_code/${encodeURIComponent(hsCode)}`);
      const desc = data?.data?.description || '';
      setFormData(prev => {
        const items = [...prev.items];
        const cur = items[idx] || {};
        items[idx] = { ...cur, hsCode, productDescription: desc };
        return { ...prev, items };
      });
      // UoM options are shown from global reference_data.uom list
    } catch (e) {
      console.warn('HS Code lookup failed', hsCode, e);
    }
  };

  // When loading an invoice or when items mount/change, ensure HS description/UoM options are populated
  useEffect(() => {
    formData.items?.forEach((it, idx) => {
      if (it?.hsCode) {
        resolveHSCode(idx, it.hsCode);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  // Auto-recalculate items when scenario changes
  useEffect(() => {
    if (formData.scenarioId && formData.items.length > 0) {
      const recalc = async () => {
        const meta = await fetchScenarioMeta(formData.scenarioId);
        const recalculatedItems = formData.items.map(item =>
          autoCalculateItem(item, formData.scenarioId, meta)
        );
        // Only update if calculations actually changed
        const hasChanges = recalculatedItems.some((newItem, index) => {
          const oldItem = formData.items[index];
          return (
            newItem.salesTaxApplicable !== oldItem.salesTaxApplicable ||
            newItem.totalValues !== oldItem.totalValues ||
            newItem.valueSalesExcludingST !== oldItem.valueSalesExcludingST ||
            newItem.rate !== oldItem.rate ||
            newItem.saleType !== oldItem.saleType
          );
        });
        if (hasChanges) {
          setFormData(prev => ({ ...prev, items: recalculatedItems }));
        }
      };
      recalc();

      

      
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.scenarioId]);


  // Safe JSON parse helper
  const safeParse = (str) => {
    try { return JSON.parse(str); } catch { return {}; }
  };

  // Display-only mapping for SN rate text shown in the Rate field
  const scenarioRateText = {
    'SN001': '18%', 'SN002': '18%', 'SN003': '18%', 'SN004': '18%',
    'SN005': '1%', 'SN006': '0%', 'SN007': '0%', 'SN008': '18%', 'SN009': '18%',
    'SN010': '17%', 'SN011': '18%', 'SN012': '1.43%', 'SN013': '5%', 'SN014': '18%', 'SN015': '18%',
    'SN016': '5%', 'SN017': '8%', 'SN018': '8%', 'SN019': '5%', 'SN020': '1%',
    'SN021': 'Rs. 3', 'SN022': '18% + Rs 60 /KG', 'SN023': '17%',
    'SN024': '25%', 'SN025': '0%', 'SN026': '18%', 'SN027': '18%', 'SN028': '1%'
  };
  const getScenarioRateText = (sid) => scenarioRateText[sid] || '18%';


  // Round to 2 decimal places
  const roundToTwoDecimals = (value) => {
  // Display-only mapping for SN rate text shown in the Rate field
  // Legacy display-only rate map left here for backwards compatibility if needed
  // Prefer DB scenario rate text fetched via fetchScenarioMeta

    if (value === null || value === undefined || value === '') return 0;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 0;
    return Math.round(numValue * 100) / 100;
  };

  // Helpers to parse DB scenario formula and rate
  const parsePercentFromRateText = (rateText) => {
    if (!rateText) return 0;
    const m = String(rateText).match(/([0-9]+(?:\.[0-9]+)?)\s*%/);
    return m ? parseFloat(m[1]) : 0;
  };
  const extractFixedPerUnitFromFormula = (formulaText) => {
    if (!formulaText) return 0;
    const m = String(formulaText).match(/Rs\.?\s*([0-9]+(?:\.[0-9]+)?)/i);
    return m ? parseFloat(m[1]) : 0;
  };

  // Calculate sales tax based on DB-backed scenario (formula + rate text)
  const calculateSalesTaxByScenario = (item, scenarioId, scenarioMeta) => {
    const rateText = scenarioMeta?.rate || '18%';
    const formulaText = scenarioMeta?.formula || '';

    const percent = parsePercentFromRateText(rateText);
    const fixedPerUnit = extractFixedPerUnitFromFormula(formulaText);

    const quantity = roundToTwoDecimals(item.quantity || 1);

    // 3rd Schedule Goods: tax on fixed notified value
    const isThirdSchedule = scenarioId === 'SN008' || scenarioId === 'SN027' || (scenarioMeta?.saleType || '').toLowerCase().includes('3rd schedule');
    if (isThirdSchedule) {
      // Base for third schedule is fixedNotifiedValueOrRetailPrice
      const base = roundToTwoDecimals(item.fixedNotifiedValueOrRetailPrice || 0);
      const pctTax = roundToTwoDecimals((base * percent) / 100);
      const fixedTax = roundToTwoDecimals(quantity * fixedPerUnit);
      return roundToTwoDecimals(pctTax + fixedTax);
    }

    // Standard base is valueSalesExcludingST
    const base = roundToTwoDecimals(item.valueSalesExcludingST || 0);
    const pctTax = roundToTwoDecimals((base * percent) / 100);
    const fixedTax = roundToTwoDecimals(quantity * fixedPerUnit);

    // If neither percent nor Rs detected, default to percent on base using percent from rate text
    const hasPercent = /%/.test(rateText) || /percent|percentage/i.test(formulaText);
    if (!hasPercent && fixedPerUnit === 0) {
      return roundToTwoDecimals((base * percent) / 100);
    }

    return roundToTwoDecimals(pctTax + fixedTax);
  };

  // Cache for DB scenarios to avoid repeated API calls
  const scenarioCache = {};
  const fetchScenarioMeta = async (scenarioId) => {
    if (scenarioCache[scenarioId]) return scenarioCache[scenarioId];
    try {
      // Prefer DB overrides; fall back to official config if not found
      const res = await apiClient.get(`/scenarios/db/${scenarioId}`);
      scenarioCache[scenarioId] = res.data?.scenario || {};
      return scenarioCache[scenarioId];
    } catch (e) {
      // Fallback: use official mapping endpoint if needed in future
      console.warn('Using fallback scenario meta for', scenarioId, e?.message);
      return {};
    }
  };

  // Auto-calculate item values based on scenario
  const autoCalculateItem = (item, scenarioId, scenarioMeta) => {
    const quantity = roundToTwoDecimals(item.quantity || 1);
    const singleUnitPrice = roundToTwoDecimals(item.singleUnitPrice || 0);

    const meta = scenarioMeta || {};
    const rateText = meta.rate || '18%'; // text field like '18%' or '18% + Rs 60 /KG'
    const saleType = meta.saleType || 'Goods at standard rate (default)';

    // Calculate base values
    const valueSalesExcludingST = roundToTwoDecimals(quantity * singleUnitPrice);
    const salesTaxApplicable = calculateSalesTaxByScenario({
      ...item,
      valueSalesExcludingST
    }, scenarioId, meta);
    const totalValues = roundToTwoDecimals(valueSalesExcludingST + salesTaxApplicable);

    // For 3rd Schedule Goods, set fixedNotifiedValueOrRetailPrice
    let fixedNotifiedValueOrRetailPrice = item.fixedNotifiedValueOrRetailPrice || 0;
    if (scenarioId === 'SN008' || scenarioId === 'SN027' || (saleType || '').toLowerCase().includes('3rd schedule')) {
      fixedNotifiedValueOrRetailPrice = totalValues; // MRP = Total value for 3rd schedule
    }

    return {
      ...item,
      quantity,
      singleUnitPrice,
      rate: rateText, // use scenarios.rate text
      valueSalesExcludingST,
      salesTaxApplicable,
      totalValues,
      fixedNotifiedValueOrRetailPrice: roundToTwoDecimals(fixedNotifiedValueOrRetailPrice),
      unitPriceExcludingTax: singleUnitPrice,
      priceIncludesTax: totalValues > valueSalesExcludingST,
      saleType
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const updatedItems = prev.items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };

          // Auto-recalculate if quantity, singleUnitPrice, or fixedNotifiedValueOrRetailPrice changes
          if (field === 'quantity' || field === 'singleUnitPrice' || field === 'fixedNotifiedValueOrRetailPrice') {
            // use latest scenario meta
            return autoCalculateItem(updatedItem, prev.scenarioId, scenarioCache[prev.scenarioId]);
          }

          return updatedItem;
        }
        return item;
      });

      return {
        ...prev,
        items: updatedItems
      };
    });
  };

  const addItem = () => {
    setFormData(prev => {
      const newItem = {
        hsCode: "",
        productDescription: "",
        rate: "18%",
        uoM: "units",
        quantity: 1,
        singleUnitPrice: 0,
        totalValues: 0,
        valueSalesExcludingST: 0,
        fixedNotifiedValueOrRetailPrice: 0,
        salesTaxApplicable: 0,
        salesTaxWithheldAtSource: 0,
        extraTax: 0,
        furtherTax: 0,
        sroScheduleNo: "",
        fedPayable: 0,
        discount: 0,
        saleType: "Goods at standard rate (default)",
        sroItemSerialNo: "",
        unitPriceExcludingTax: 0,
        priceIncludesTax: false
      };

      // Auto-calculate the new item based on current scenario
      const calculatedItem = autoCalculateItem(newItem, prev.scenarioId, scenarioCache[prev.scenarioId]);

      return {
        ...prev,
        items: [...prev.items, calculatedItem]
      };
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const validateInvoice = async () => {
    // Validation for Debit Note
    if (formData.invoiceType === 'Debit Note' && !formData.invoiceRefNo) {
      toast.error('Invoice Reference Number is required for Debit Note');
      return;
    }

    try {
      setValidating(true);

      // Prepare the invoice data for validation
      const invoiceData = {
        ...formData,
        items: (formData.items || []).map((it) => {
          const { salesTaxRate, ratePercentage, unitPriceExcludingTax, ...rest } = it || {};
          const rateText = typeof rest.rate === 'string' ? rest.rate : getScenarioRateText(formData.scenarioId);
          return {
            ...rest,
            rate: rateText,
            quantity: Number(rest.quantity || 0),
            totalValues: Number(rest.totalValues || 0),
            valueSalesExcludingST: Number(rest.valueSalesExcludingST || 0),
            fixedNotifiedValueOrRetailPrice: Number(rest.fixedNotifiedValueOrRetailPrice || 0),
            salesTaxApplicable: Number(rest.salesTaxApplicable || 0),
            salesTaxWithheldAtSource: Number(rest.salesTaxWithheldAtSource || 0),
            fedPayable: Number(rest.fedPayable || 0),
            discount: Number(rest.discount || 0)
          };
        })
      };

      // Console log the request data
      console.group('🔍 FBR VALIDATION REQUEST');
      console.log('📤 Request Data Sent to FBR:', invoiceData);
      console.log('🕐 Timestamp:', new Date().toISOString());
      console.groupEnd();

      // Ensure DB has latest JSON before validation
      if (isEdit && id) {
        await apiClient.put(`/invoice/${id}`, invoiceData);
      }

      // Call FBR validation endpoint directly to get detailed response
      const response = await apiClient.post(`/fbr-validate/validate`, {
        invoiceData,
        invoiceId: id ? parseInt(id, 10) : undefined
      });

      // Console log the response data
      console.group('📥 FBR VALIDATION RESPONSE');
      console.log('✅ Response Data from FBR:', response.data);
      console.log('🕐 Timestamp:', new Date().toISOString());
      console.groupEnd();

      // Show API Response Modal
      setApiModalData({
        type: 'validate',
        requestData: invoiceData,
        responseData: response.data,
        success: response.data?.success,
        timestamp: new Date().toISOString()
      });
      setShowAPIModal(true);

      if (response.data?.success) {
        toast.success('Invoice validated successfully with FBR!');
        setInvoice(prev => ({ ...(prev || { id }), status: 'Validated' }));
      }

    } catch (error) {
      console.group('❌ FBR VALIDATION ERROR');
      console.error('Validation failed:', error);
      console.log('📤 Request Data that Failed:', formData);
      console.log('📥 Error Response:', error.response?.data);
      console.log('🕐 Timestamp:', new Date().toISOString());
      console.groupEnd();

      const data = error.response?.data;
      const validationStatus = data?.validation?.validationResponse?.statusCode;
      const validationMsg = data?.validation?.validationResponse?.message || data?.validation?.validationResponse?.statusMessage;
      const errorMessage = data?.message || validationMsg || `FBR validation returned status ${validationStatus || 'unknown'}`;

      // Show API Response Modal even for errors
      setApiModalData({
        type: 'validate',
        requestData: formData,
        responseData: data,
        success: false,
        timestamp: new Date().toISOString()
      });
      setShowAPIModal(true);

      toast.error(`Validation failed: ${errorMessage}`);
    } finally {
      setValidating(false);
    }
  };

  const submitInvoice = async () => {
    // Validation for Debit Note
    if (formData.invoiceType === 'Debit Note' && !formData.invoiceRefNo) {
      toast.error('Invoice Reference Number is required for Debit Note');
      return;
    }

    try {
      setSubmitting(true);

      // Prepare the invoice data for submission
      const invoiceData = {
        ...formData,
        items: (formData.items || []).map((it) => {
          const { salesTaxRate, ratePercentage, unitPriceExcludingTax, ...rest } = it || {};
          const rateText = typeof rest.rate === 'string' ? rest.rate : getScenarioRateText(formData.scenarioId);
          return {
            ...rest,
            rate: rateText,
            quantity: Number(rest.quantity || 0),
            totalValues: Number(rest.totalValues || 0),
            valueSalesExcludingST: Number(rest.valueSalesExcludingST || 0),
            fixedNotifiedValueOrRetailPrice: Number(rest.fixedNotifiedValueOrRetailPrice || 0),
            salesTaxApplicable: Number(rest.salesTaxApplicable || 0),
            salesTaxWithheldAtSource: Number(rest.salesTaxWithheldAtSource || 0),
            fedPayable: Number(rest.fedPayable || 0),
            discount: Number(rest.discount || 0)
          };
        })
      };

      const requestPayload = {
        invoiceData,
        invoiceId: invoice?.id || id ? parseInt(invoice?.id || id, 10) : undefined,
        validateFirst: (invoice?.status !== 'Validated')
      };

      // Console log the request data
      console.group('🚀 FBR SUBMISSION REQUEST');
      console.log('📤 Request Data Sent to FBR:', requestPayload);
      console.log('🕐 Timestamp:', new Date().toISOString());
      console.groupEnd();

      // Submit to FBR via test endpoint (optionally validates first)
      const response = await apiClient.post(`/fbr-validate/submit`, requestPayload);

      // Console log the response data
      console.group('📥 FBR SUBMISSION RESPONSE');
      console.log('✅ Response Data from FBR:', response.data);
      console.log('🕐 Timestamp:', new Date().toISOString());
      console.groupEnd();

      // Show API Response Modal
      setApiModalData({
        type: 'submit',
        requestData: requestPayload,
        responseData: response.data,
        success: response.data?.success,
        timestamp: new Date().toISOString()
      });
      setShowAPIModal(true);

      if (response.data.success) {
        toast.success('Invoice submitted to FBR successfully!');
        setInvoice(prev => ({ ...(prev || { id }), status: 'Submitted' }));
      }

    } catch (error) {
      console.group('❌ FBR SUBMISSION ERROR');
      console.error('Submission failed:', error);
      console.log('📤 Request Data that Failed:', {
        invoiceData: formData,
        invoiceId: invoice?.id || id ? parseInt(invoice?.id || id, 10) : undefined,
        validateFirst: (invoice?.status !== 'Validated')
      });
      console.log('📥 Error Response:', error.response?.data);
      console.log('🕐 Timestamp:', new Date().toISOString());
      console.groupEnd();

      const data = error.response?.data;
      const submissionStatus = data?.submission?.submissionResponse?.statusCode || data?.submission?.statusCode;
      const submissionMsg = data?.submission?.submissionResponse?.message || data?.submission?.message;
      const errorMessage = data?.message || submissionMsg || `FBR submission returned status ${submissionStatus || 'unknown'}`;

      // Show API Response Modal even for errors
      setApiModalData({
        type: 'submit',
        requestData: {
          invoiceData: formData,
          invoiceId: invoice?.id || id ? parseInt(invoice?.id || id, 10) : undefined,
          validateFirst: (invoice?.status !== 'Validated')
        },
        responseData: data,
        success: false,
        timestamp: new Date().toISOString()
      });
      setShowAPIModal(true);

      toast.error(`Submission failed: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const saveInvoice = async () => {
    try {
      setLoading(true);

      // Prepare payload matching backend validation expectations
      const payload = {
        ...formData,
        items: (formData.items || []).map((it) => {
          const { salesTaxRate, ratePercentage, unitPriceExcludingTax, ...rest } = it || {};
          const rateText = typeof rest.rate === 'string' ? rest.rate : getScenarioRateText(formData.scenarioId);
          return {
            ...rest,
            rate: rateText,
            quantity: Number(rest.quantity || 0),
            totalValues: Number(rest.totalValues || 0),
            valueSalesExcludingST: Number(rest.valueSalesExcludingST || 0),
            fixedNotifiedValueOrRetailPrice: Number(rest.fixedNotifiedValueOrRetailPrice || 0),
            salesTaxApplicable: Number(rest.salesTaxApplicable || 0),
            salesTaxWithheldAtSource: Number(rest.salesTaxWithheldAtSource || 0),
            fedPayable: Number(rest.fedPayable || 0),
            discount: Number(rest.discount || 0)
          };
        })
      };

      let response;
      if (isEdit) {
        response = await apiClient.put(`/invoice/${id}`, payload);
        toast.success('Invoice updated successfully');
      } else {
        response = await apiClient.post('/invoice', payload);
        toast.success('Invoice created successfully');
        navigate(`/invoice/${response.data.invoice.id}/edit`);
      }

      setInvoice(response.data.invoice);
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isEdit ? 'Edit Invoice' : 'Create New Invoice'}
        </h1>
        <p className="text-gray-600">
          Invoice form matching FBR validation requirements exactly
        </p>
        {invoice && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Status: <span className={`font-semibold ${
                invoice.status === 'Validated' ? 'text-green-600' :
                invoice.status === 'Submitted' ? 'text-blue-600' :
                invoice.status === 'Error' ? 'text-red-600' :
                'text-yellow-600'
              }`}>
                {invoice.status}
              </span>
            </p>
          </div>
        )}
      </div>

      <form className="space-y-8">
        {/* Basic Invoice Information */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Type
              </label>
              <select
                name="invoiceType"
                value={formData.invoiceType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="Sale Invoice">Sale Invoice</option>
                <option value="Debit Note">Debit Note</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Date
              </label>
              <input
                type="date"
                name="invoiceDate"
                value={formData.invoiceDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Reference No
              </label>
              <input
                type="text"
                name="invoiceRefNo"
                value={formData.invoiceRefNo}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scenario ID
              </label>
              <input
                type="text"
                name="scenarioId"
                value={formData.scenarioId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                readOnly
              />
            </div>
          </div>

          {/* Scenario Information */}
          {formData.scenarioId && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Scenario Calculation Rules:</h3>
              <div className="text-sm text-blue-800">
                {(() => {
                  const taxRates = {
                    'SN001': '18%', 'SN002': '18%', 'SN003': '18%', 'SN004': '18%', 'SN005': '10%', 'SN006': '0%', 'SN007': '0%',
                    'SN008': '18%', 'SN009': '18%', 'SN010': '19.5%', 'SN011': '18%', 'SN012': '17%', 'SN013': '17%', 'SN014': '17%',
                    'SN015': '18%', 'SN016': '18%', 'SN017': '18%', 'SN018': '18%', 'SN019': '16%', 'SN020': '1%', 'SN021': '18%',
                    'SN022': '18%', 'SN023': '17%', 'SN024': '18%', 'SN025': '16%', 'SN026': '18%', 'SN027': '18%', 'SN028': '10%'
                  };
                  const scenarioNames = {
                    'SN001': 'Standard Rate Goods to Registered Buyers',
                    'SN002': 'Standard Rate Goods to Unregistered Buyers',
                    'SN005': 'Reduced Rate Goods (Eighth Schedule)',
                    'SN006': 'Exempt Goods (Sixth Schedule)',
                    'SN007': 'Zero-Rated Goods (Fifth Schedule)',
                    'SN008': '3rd Schedule Goods',
                    'SN020': 'Electric Vehicles',
                    'SN027': '3rd Schedule Goods to End Consumers',
                    'SN028': 'Reduced Rate Goods to End Consumers'
                  };
                  const taxRate = taxRates[formData.scenarioId] || '18%';
                  const scenarioName = scenarioNames[formData.scenarioId] || 'Standard Rate Goods';
                  const isThirdSchedule = formData.scenarioId === 'SN008' || formData.scenarioId === 'SN027';

                  return (
                    <div>
                      <div><strong>Scenario:</strong> {scenarioName}</div>
                      <div><strong>Tax Rate:</strong> {taxRate}</div>
                      <div><strong>Calculation Method:</strong> {isThirdSchedule ? 'Tax calculated on Fixed/Notified Value' : 'Tax calculated on transaction value'}</div>
                      <div className="mt-2 text-xs">
                        <strong>Formula:</strong> {isThirdSchedule ? 'Tax = Fixed Value × Tax Rate' : 'Tax = (Quantity × Unit Price) × Tax Rate'}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Seller Information */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Seller Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seller NTN/CNIC
              </label>
              <input
                type="text"
                name="sellerNTNCNIC"
                value={formData.sellerNTNCNIC}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name
              </label>
              <input
                type="text"
                name="sellerBusinessName"
                value={formData.sellerBusinessName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Province
              </label>
              <input
                type="text"
                name="sellerProvince"
                value={formData.sellerProvince}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                name="sellerAddress"
                value={formData.sellerAddress}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Buyer Information */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Buyer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buyer NTN/CNIC
              </label>
              <input
                type="text"
                name="buyerNTNCNIC"
                value={formData.buyerNTNCNIC}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name
              </label>
              <input
                type="text"
                name="buyerBusinessName"
                value={formData.buyerBusinessName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Province
              </label>
              <input
                type="text"
                name="buyerProvince"
                value={formData.buyerProvince}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                name="buyerAddress"
                value={formData.buyerAddress}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Registration Type</label>
              <select
                name="buyerRegistrationType"
                value={formData.buyerRegistrationType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {['Registered','Unregistered','Unregistered Distributor','Retail Consumer'].map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Invoice Items */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Invoice Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Item
            </button>
          </div>

          {formData.items.map((item, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Item {index + 1}</h3>
                {formData.items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">HS Code</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={item.hsCode}
                      onChange={async (e) => {
                        const val = e.target.value;
                        handleItemChange(index, 'hsCode', val);
                        if (val && val.length >= 4) {
                          try {
                            const { data } = await apiClient.get(`/reference/hs-codes?search=${encodeURIComponent(val)}&limit=10`);
                            setHsSuggestions(prev => ({ ...prev, [index]: data?.data || [] }));
                          } catch {}
                        } else {
                          setHsSuggestions(prev => ({ ...prev, [index]: [] }));
                        }
                      }}
                      onBlur={() => {
                        // When user leaves the field, resolve HS to description and UoM
                        resolveHSCode(index, item.hsCode);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Type to search HS Code"
                    />
                    {/* Suggestions dropdown */}
                    {Array.isArray(hsSuggestions[index]) && hsSuggestions[index].length > 0 && (
                      <div className="absolute z-10 bg-white border border-gray-200 mt-1 rounded shadow max-h-48 overflow-auto w-full">
                        {hsSuggestions[index].map((sug) => (
                          <div
                            key={sug.code}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleItemChange(index, 'hsCode', sug.code);
                              setHsSuggestions(prev => ({ ...prev, [index]: [] }));
                              resolveHSCode(index, sug.code);
                            }}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                          >
                            <span className="font-mono mr-2">{sug.code}</span>
                            <span className="text-gray-600">{sug.description}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Description
                  </label>
                  <input
                    type="text"
                    value={item.productDescription}
                    onChange={(e) => handleItemChange(index, 'productDescription', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UoM</label>
                  <select
                    value={item.uoM}
                    onChange={(e) => {
                      handleItemChange(index, 'uoM', e.target.value);
                      validateUoMForItem(index);
                    }}
                    onFocus={() => {
                      if (item.hsCode) fetchUoMOptions(index, item.hsCode);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {(() => {
                      const current = (item.uoM ?? '').toString();
                      const inList = uomOptionsGlobal.some(opt => (typeof opt === 'string' ? opt : (opt?.value || opt?.code)) === current);
                      return !inList && current ? (
                        <option value={current}>{current}</option>
                      ) : null;
                    })()}
                    {uomOptionsGlobal.map(opt => {
                      const value = typeof opt === 'string' ? opt : (opt?.value || opt?.code);
                      const label = typeof opt === 'string' ? opt : (opt?.label || opt?.code || '');
                      return (
                        <option key={value} value={value}>{label}</option>
                      );
                    })}
                  </select>
                  {uomErrors[index] && (
                    <div className="mt-1 text-xs text-red-600">
                      Invalid UoM for HS Code. Valid: {(uomErrors[index].validUoMs || []).join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit Price (Excl. Tax)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.singleUnitPrice || 0}
                    onChange={(e) => handleItemChange(index, 'singleUnitPrice', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate
                  </label>
                  <input
                    type="text"
                    value={getScenarioRateText(formData.scenarioId)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Values <span className="text-xs text-blue-600">(Auto-calculated)</span>
                  </label>
                  <input
                    type="number"
                    value={item.totalValues}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value Sales Excluding ST <span className="text-xs text-blue-600">(Auto-calculated)</span>
                  </label>
                  <input
                    type="number"
                    value={item.valueSalesExcludingST}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fixed/Notified Value
                    {(formData.scenarioId === 'SN008' || formData.scenarioId === 'SN027') &&
                      <span className="text-xs text-red-600">(Required for 3rd Schedule)</span>
                    }
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.fixedNotifiedValueOrRetailPrice}
                    onChange={(e) => handleItemChange(index, 'fixedNotifiedValueOrRetailPrice', parseFloat(e.target.value) || 0)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      (formData.scenarioId === 'SN008' || formData.scenarioId === 'SN027') ? 'border-red-300' : ''
                    }`}
                    placeholder={(formData.scenarioId === 'SN008' || formData.scenarioId === 'SN027') ? 'Enter MRP/Notified Value' : ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sales Tax Applicable <span className="text-xs text-blue-600">(Auto-calculated)</span>
                  </label>
                  <input
                    type="number"
                    value={item.salesTaxApplicable}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sale Type <span className="text-xs text-blue-600">(Auto-set by scenario)</span>
                  </label>
                  <input
                    type="text"
                    value={item.saleType}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                  />
                </div>

                {/* Calculation Summary */}
                <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-200">
                  <div className="text-sm text-blue-800">
                    <div className="font-medium mb-1">Calculation Summary:</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>Qty × Unit Price = {item.quantity} × {item.singleUnitPrice} = {item.valueSalesExcludingST}</div>
                      <div>Tax ({item.rate}) = {item.salesTaxApplicable}</div>
                      <div className="font-medium">Total = {item.totalValues}</div>
                      <div>Scenario: {formData.scenarioId}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 pt-6 border-t">
          <button
            type="button"
            onClick={saveInvoice}
            disabled={loading}
            className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Invoice'}
          </button>

          <button
            type="button"
            onClick={validateInvoice}
            disabled={validating}
            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {validating ? 'Validating...' : 'Validate with FBR'}
          </button>

          <button
            type="button"
            onClick={submitInvoice}
            disabled={submitting}
            className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit to FBR'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/invoices')}
            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* API Response Modal */}
      <APIResponseModal
        isOpen={showAPIModal}
        onClose={() => setShowAPIModal(false)}
        data={apiModalData}
      />
    </div>
  );
};

export default InvoiceForm;
